import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { InvalidPersonalDataErrorApiAdapter } from '../../../adapters/InvalidPersonalDataErrorApiAdapter';
import { translateOrderApiError } from '../../../../CreateEditOrders/utils/errorTranslator';

export interface InvalidPersonalDataErrorModalRef {
  openModal: (orderId: number) => void;
  closeModal: () => void;
}

interface InvalidPersonalDataErrorModalProps {
  customer?: {
    name?: string;
    email?: string;
    phone?: string;
  };
  onSuccess?: () => void;
}

const InvalidPersonalDataErrorModalComponent: React.ForwardRefRenderFunction<
  InvalidPersonalDataErrorModalRef,
  InvalidPersonalDataErrorModalProps
> = ({ customer, onSuccess }, ref) => {
  const { t } = useTranslation('viewOrdersModule');
  const { toast } = useToast();
  const modalRef = useRef<ModalCenterRef>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        email: customer.email || '',
        phone: customer.phone || '',
      });
    }
  }, [customer]);

  useImperativeHandle(ref, () => ({
    openModal: (orderId: number) => {
      setCurrentOrderId(orderId);
      modalRef.current?.openModal();
    },
    closeModal: () => modalRef.current?.closeModal(),
  }));

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClose = () => {
    modalRef.current?.closeModal();
  };

  const handleBack = () => {
    modalRef.current?.closeModal();
  };

  const handleSave = async () => {
    if (!currentOrderId) {
      toast({
        title: t('invalidPersonalDataError.error.title'),
        description: 'ID del pedido no encontrado',
        variant: 'destructive',
      });
      return;
    }

    // Validar campos requeridos
    if (!formData.name || !formData.phone) {
      toast({
        title: t('invalidPersonalDataError.error.title'),
        description: 'Debes completar todos los campos requeridos',
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new InvalidPersonalDataErrorApiAdapter(httpClient);

      const requestData = {
        customer: {
          name: formData.name,
          phone: formData.phone,
          email: formData.email || undefined,
        },
      };

      console.log('🔍 InvalidPersonalDataErrorModal - Sending data:', {
        orderId: currentOrderId,
        data: requestData,
      });

      const response = await adapter.solveInvalidPersonalDataError(currentOrderId, requestData);

      console.log('🔍 InvalidPersonalDataErrorModal - Response:', response);

      if (response.success) {
        toast({
          title: t('invalidPersonalDataError.success.title'),
          description: response.message,
          variant: 'default',
        });

        // Ejecutar callback de éxito si existe
        onSuccess?.();
      } else {
        toast({
          title: t('invalidPersonalDataError.error.title'),
          description: response.message,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      // Traducir el error usando el sistema de traducción
      const translatedError = translateOrderApiError(error, t);

      toast({
        title: t('invalidPersonalDataError.error.title'),
        description: translatedError,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalCenter
      ref={modalRef}
      title={t('invalidPersonalDataError.title')}
      icon={
        <button
          onClick={handleBack}
          className="flex items-center justify-center w-6 h-6 hover:bg-hover-press rounded transition-colors"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
        </button>
      }
      width="lg"
      type="default"
      fullScreenMobile={true}
      className="max-h-[90vh]"
    >
      <div className="flex flex-col h-full">
        {/* Contenido principal */}
        <div className="flex-1 flex flex-col gap-6 py-4 text-sm max-h-[70vh] overflow-y-auto pb-24 md:pb-4">
          {/* Descripción del error */}
          <p className="text-sm">{t('invalidPersonalDataError.description')}</p>

          <div className="flex flex-col gap-4">
            <div>
              <label htmlFor="name" className="font-medium">
                {t('invalidPersonalDataError.name')} <span className="text-error">*</span>
              </label>
              <Input
                id="name"
                placeholder={t('invalidPersonalDataError.placeholders.name')}
                className="mt-1"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="email" className="font-medium">
                  {t('invalidPersonalDataError.email')}
                </label>
                <Input
                  id="email"
                  placeholder={t('invalidPersonalDataError.placeholders.email')}
                  type="email"
                  className="mt-1"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="phone" className="font-medium">
                  {t('invalidPersonalDataError.phone')} <span className="text-error">*</span>
                </label>
                <Input
                  id="phone"
                  placeholder={t('invalidPersonalDataError.placeholders.phone')}
                  type="tel"
                  className="mt-1"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Botones de acción - Fijos en mobile */}
        <div className="fixed bottom-0 left-0 right-0 p-4 md:pb-0 md:px-0 md:relative md:border-t-0">
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="lg" onClick={handleClose}>
              {t('invalidPersonalDataError.cancel')}
            </Button>
            <Button variant="primary" size="lg" onClick={handleSave} disabled={isSubmitting}>
              {isSubmitting ? t('invalidPersonalDataError.submitting') : t('invalidPersonalDataError.save')}
            </Button>
          </div>
        </div>
      </div>
    </ModalCenter>
  );
};

export const InvalidPersonalDataErrorModal = forwardRef(InvalidPersonalDataErrorModalComponent);

InvalidPersonalDataErrorModal.displayName = 'InvalidPersonalDataErrorModal';
